/**
 * Cuerpos de request de la API (SSOT S1). El front los usa para validar formularios y
 * el back para validar el borde: un solo schema, cero duplicación.
 */
import { z } from 'zod';
import { CentsWireSchema, PositiveCentsWireSchema } from '../money.js';
import { CornerSchema, FighterSnapshotSchema, FightModeSchema } from '../domain/fight.js';
import { PresetParamsSchema, PresetStatusSchema, IdentificacionSchema } from '../domain/preset.js';
import { PoliticaDesbalanceSchema, RulesSnapshotSchema } from '../domain/rules.js';
import { RoleSchema } from '../roles.js';
import { UserStatusSchema, TerminalKindSchema } from '../domain/org.js';

/* ------------------------------- Auth ------------------------------------ */

export const RequestOtpBody = z.object({
  email: z.string().email().max(160),
});
export type RequestOtpBody = z.infer<typeof RequestOtpBody>;

export const VerifyOtpBody = z.object({
  email: z.string().email().max(160),
  code: z.string().regex(/^\d{6}$/, 'El código tiene 6 dígitos'),
  terminal: z
    .object({
      id: z.string().min(1).max(64),
      label: z.string().min(1).max(60),
      kind: TerminalKindSchema,
    })
    .optional(),
});
export type VerifyOtpBody = z.infer<typeof VerifyOtpBody>;

export const PasswordLoginBody = z.object({
  email: z.string().email().max(160),
  password: z.string().min(8).max(128),
  terminal: z
    .object({
      id: z.string().min(1).max(64),
      label: z.string().min(1).max(60),
      kind: TerminalKindSchema,
    })
    .optional(),
});
export type PasswordLoginBody = z.infer<typeof PasswordLoginBody>;

/** Acceso instantáneo con el código de administración rotativo. */
export const AccessCodeLoginBody = z.object({
  code: z.string().min(8).max(64),
  label: z.string().min(1).max(60).optional(),
  terminal: z
    .object({
      id: z.string().min(1).max(64),
      label: z.string().min(1).max(60),
      kind: TerminalKindSchema,
    })
    .optional(),
});
export type AccessCodeLoginBody = z.infer<typeof AccessCodeLoginBody>;

/** Pago masivo de TODOS los pendientes (sin filtrar por pelea). */
export const SettleAllPendingBody = z.object({
  limit: z.number().int().min(1).max(2000).default(500),
  reason: z.string().max(200).optional(),
  terminalId: z.string().max(64).optional(),
});
export type SettleAllPendingBody = z.infer<typeof SettleAllPendingBody>;

export const PatchMeBody = z.object({
  displayName: z.string().min(1).max(120).optional(),
  citizenName: z.string().max(80).nullable().optional(),
});
export type PatchMeBody = z.infer<typeof PatchMeBody>;

/* ------------------------------ Peleas ----------------------------------- */

/** Overrides de reglas: se validan y se congelan en el snapshot. */
export const RulesOverrideSchema = RulesSnapshotSchema.partial().omit({
  presetId: true,
  presetSemver: true,
  engineVersion: true,
  paramsHash: true,
});
export type RulesOverride = z.infer<typeof RulesOverrideSchema>;

export const CreateFightBody = z.object({
  title: z.string().min(1).max(120),
  mode: FightModeSchema.default('1v1'),
  redFighter: FighterSnapshotSchema,
  blueFighter: FighterSnapshotSchema,
  presetId: z.string().min(3).max(64),
  rulesOverride: RulesOverrideSchema.optional(),
});
export type CreateFightBody = z.infer<typeof CreateFightBody>;

export const UpdateFightBody = z.object({
  title: z.string().min(1).max(120).optional(),
  redFighter: FighterSnapshotSchema.optional(),
  blueFighter: FighterSnapshotSchema.optional(),
  version: z.number().int().positive(),
});
export type UpdateFightBody = z.infer<typeof UpdateFightBody>;

export const VersionedActionBody = z.object({
  version: z.number().int().positive(),
});
export type VersionedActionBody = z.infer<typeof VersionedActionBody>;

export const SettleFightBody = z.object({
  version: z.number().int().positive(),
  winner: CornerSchema,
});
export type SettleFightBody = z.infer<typeof SettleFightBody>;

export const CancelFightBody = z.object({
  version: z.number().int().positive(),
  reason: z.string().min(3).max(240),
});
export type CancelFightBody = z.infer<typeof CancelFightBody>;

/* ------------------------------ Boletos ---------------------------------- */

export const CreateBetBody = z
  .object({
    corner: CornerSchema,
    stakeCents: PositiveCentsWireSchema,
    /** Modo ventanilla: nombre del ciudadano. */
    citizenName: z.string().min(1).max(80).optional(),
    /** Modo cuenta: id del usuario apostador. */
    userId: z.string().min(1).max(64).optional(),
    terminalId: z.string().max(64).optional(),
    /** Si el operador fuerza la aceptación pese al desbalance. */
    forceAccept: z.boolean().default(false),
  })
  .refine((v) => Boolean(v.citizenName) !== Boolean(v.userId), {
    message: 'Indicá exactamente uno: citizenName o userId',
    path: ['citizenName'],
  });
export type CreateBetBody = z.infer<typeof CreateBetBody>;

export const PayBetBody = z.object({
  terminalId: z.string().max(64).optional(),
  reason: z.string().max(240).optional(),
});
export type PayBetBody = z.infer<typeof PayBetBody>;

export const SettleAllBody = z.object({
  terminalId: z.string().max(64).optional(),
  reason: z.string().max(240).optional(),
  /** Tope opcional de boletos a pagar en esta llamada (para no bloquear la caja). */
  limit: z.number().int().min(1).max(1000).default(500),
});
export type SettleAllBody = z.infer<typeof SettleAllBody>;

export const LookupBetQuery = z.object({
  q: z.string().min(3).max(120),
  fightId: z.string().length(26).optional(),
  status: z.enum(['any', 'payable', 'open']).default('any'),
});
export type LookupBetQuery = z.infer<typeof LookupBetQuery>;

/* ------------------------------ Presets ---------------------------------- */

export const UpsertPresetBody = z.object({
  semver: z.string().regex(/^\d+\.\d+\.\d+$/),
  name: z.string().min(3).max(120),
  explanationMd: z.string().min(20),
  params: PresetParamsSchema,
  requisitosDatos: z.array(z.string().max(120)).max(20).default([]),
  identificacion: IdentificacionSchema,
});
export type UpsertPresetBody = z.infer<typeof UpsertPresetBody>;

export const ApprovePresetBody = z.object({
  to: PresetStatusSchema,
  identificacion: IdentificacionSchema.optional(),
});
export type ApprovePresetBody = z.infer<typeof ApprovePresetBody>;

/* ------------------------------ Usuarios --------------------------------- */

export const UpdateUserBody = z
  .object({
    role: RoleSchema.optional(),
    status: UserStatusSchema.optional(),
    displayName: z.string().min(1).max(120).optional(),
    citizenName: z.string().max(80).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nada para actualizar' });
export type UpdateUserBody = z.infer<typeof UpdateUserBody>;

/* ---------------------------- Terminales --------------------------------- */

export const HeartbeatBody = z.object({
  terminalId: z.string().min(1).max(64),
  label: z.string().min(1).max(60),
  kind: TerminalKindSchema,
});
export type HeartbeatBody = z.infer<typeof HeartbeatBody>;

/* ------------------------------ Exportes --------------------------------- */

export const ExportFormatSchema = z.enum(['txt', 'json', 'csv']);
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

/** Re-export útil para la UI de ajustes. */
export const RulesInputSchema = RulesSnapshotSchema.omit({
  presetId: true,
  presetSemver: true,
  engineVersion: true,
  paramsHash: true,
}).extend({
  politicaDesbalance: PoliticaDesbalanceSchema,
  minStakeCents: CentsWireSchema,
});
export type RulesInput = z.infer<typeof RulesInputSchema>;
